import { cast, flow, SnapshotIn, types, toGenerator, getSnapshot } from 'mobx-state-tree'
import { CenterSettingsModel } from '../models/CenterSettings/CenterSettingsModel'
import type { Center, CenterModel } from '../models/CenterSettings/CenterModel'
import { CenterSummaryModel } from '../models/CenterSettings/CenterModel'
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatusModel, OperationSuccess } from '../models/common/OperationStatus'
import { FetchModel } from '../models/FetchModel'
import { isOversightControl } from './rootStore'
import { CENTER_NAME } from '../params'

export const CenterSettingsStore = FetchModel.named('CenterSettingsStore')
  .props({
    centerSettings: types.maybeNull(CenterSettingsModel),

    centerSettingsCopyForEditing: types.maybeNull(CenterSettingsModel),

    centers: types.maybeNull(types.array(CenterSummaryModel)),
    fetchCentersStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    sendTestEmailStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get getCentersUrl(): string {
      if (isOversightControl()) {
        return `${self.oversightBaseUrl}deployments/`
      } else {
        return `${self.baseUrl}centers/`
      }
    },
    saveCenterUrl(deploymentId: number): string {
      if (isOversightControl()) {
        return `${self.oversightBaseUrl}deployments/${deploymentId}/`
      } else {
        return `${self.baseUrl}centers/${deploymentId}/`
      }
    },
    sendTestEmailUrl(deploymentId: number): string {
      if (isOversightControl()){
        return `${self.oversightBaseUrl}deployments/${deploymentId}/send_test_email/`
      } else {
        return `${self.baseUrl}centers/${deploymentId}/send_test_email/`
      }
    },
  }))
  .actions((self) => ({
    _fetchCenters: flow(function* _fetchCenters() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<SnapshotIn<typeof CenterModel>>>(
          self.getCentersUrl,
          {},
          {fetchOperationName: 'fetch centers'} )
      )
    }),
  }))
  .actions((self) => ({
    fetchCenters: flow(function* fetchCenters() {
      self.fetchCentersStatus = OperationInProgress()

      const fetchCentersResult = yield* toGenerator(self._fetchCenters())
      if (fetchCentersResult.error) {
        self.fetchCentersStatus = OperationFailure(fetchCentersResult.error)
        return
      }

      self.centers = cast(fetchCentersResult.result.map(c => ({ id: c.id, name: c.name })))
      self.fetchCentersStatus = OperationSuccess()
    }),
    fetchAndSetCenterSettings: flow(function* fetchCenterSettings() {
      self.fetchStatus = OperationInProgress()

      const fetchCentersResult = yield* toGenerator(self._fetchCenters())
      if (fetchCentersResult.error) {
        self.fetchStatus = OperationFailure(fetchCentersResult.error)
        return
      }

      const deliveryCenter = (isOversightControl())
        ? fetchCentersResult.result.find(c => c.name === CENTER_NAME)
        : fetchCentersResult.result[0]
      if (deliveryCenter === undefined) {
        const message = isOversightControl() 
          ? `Can't find center ${CENTER_NAME}`
          : 'No centers found'
        self.fetchStatus = OperationFailure({ name:'APIError', message: message})
        return
      }

      const centerSettingsSnapshot = {
        center: deliveryCenter,
        timezones: []
      }
      self.centerSettings = CenterSettingsModel.create(centerSettingsSnapshot)
      self.centerSettingsCopyForEditing = CenterSettingsModel.create(centerSettingsSnapshot)

      self.fetchStatus = OperationSuccess()
    }),
  }))
  .actions((self) => ({
    _saveCenter: flow(function* _saveCenter(center: Center) {
      return yield* toGenerator(
        self.loadObjectOrError<void>(
          self.saveCenterUrl(center.id), 
          { method: 'PATCH', body: JSON.stringify(getSnapshot(center)) }, 
          { fetchOperationName: 'Save center' }),
      )
    }),
  }))
  .actions((self) => ({
    saveCenterSettings: flow(function* fetchCenterSettings() {
      self.saveStatus = OperationInProgress()
      if (self.centerSettingsCopyForEditing === null) {
        throw Error('Cannot save deployment settings before loading')
      }
      // TODO: what if one succeeds and another fails?
      const saveCenterResult = yield* toGenerator(self._saveCenter(self.centerSettingsCopyForEditing.center))
      if (saveCenterResult.error) {
        self.saveStatus = OperationFailure(saveCenterResult.error)
        return
      }

      self.saveStatus = OperationSuccess()
      void self.fetchAndSetCenterSettings()
    }),
    sendTestEmail: flow(function* sendTestEmail() {
      if (self.centerSettings === null) {
        throw Error('Cannot send test email before they were loaded')
      } 

      self.sendTestEmailStatus = OperationInProgress()

      const sendEmailLoadResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.sendTestEmailUrl(self.centerSettings?.center.id), 
          { method: 'POST' }, 
          { fetchOperationName: 'Send test email' }),
      )

      if (sendEmailLoadResult.error) {
        self.sendTestEmailStatus = OperationFailure(sendEmailLoadResult.error)
        return
      }

      self.sendTestEmailStatus = OperationSuccess()
    }),
  }))